import React, { Suspense, useMemo } from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import SiteLang from '@skygroup/shared/components/SiteLang';
import getImagePathFromName from '@skygroup/shared/utils/getImagePathFromName';
import importComponents from '../../utils/importComponents';
import classPriority from '../../utils/classPriority';
import useCssVariables from '../../hooks/useCssVariables';
import { updateLang, updateTranslations } from '../../store/config';
import translationsHe from '../../translations/translationsHe';
import translationsEn from '../../translations/translationsEn';
import translationsUk from '../../translations/translationsUk';
import './ChangeApprovalLayout.less';

interface Props {
  children: React.ReactNode;
}

/**
 * Lightweight layout for the public change-approval page. Renders a minimal
 * custom header (logo + language selector, mirroring the regular b2c header)
 * and the site's configured footer around the page content.
 */
const ChangeApprovalLayout: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const generateCssVariables = useCssVariables();

  const {
    config: { layouts, modulesConfigs, siteConfig, theme },
    currentLang,
    direction,
  } = useSelector((state: RootState) => state.config);

  const headerConfig = modulesConfigs?.Header;

  // Same locale switch the regular header uses.
  const changeLocale = (lang: string) => {
    dispatch(updateLang(lang));
    if (lang === 'en_US') {
      dispatch(updateTranslations(translationsEn.data.translation));
    } else if (lang === 'uk_UA') {
      dispatch(updateTranslations(translationsUk.data.translation));
    } else {
      dispatch(updateTranslations(translationsHe.data.translation));
    }
  };

  // Mirrors Header.tsx `renderSiteLang` exactly (same guard, props and styles).
  const renderSiteLang = ({
    wrapperClassName = '',
    withoutBorders = false,
  }: {
    wrapperClassName?: string;
    withoutBorders?: boolean;
  }) => {
    if (!headerConfig?.widgetLangs?.isActive) {
      return <></>;
    }
    return (
      <SiteLang
        languagesArr={siteConfig?.languages}
        defaultLang={currentLang ?? siteConfig?.defaultLang}
        changeLocale={changeLocale}
        wrapperClassName={wrapperClassName ?? 'header-site-lang'}
        withoutBorders={withoutBorders}
      />
    );
  };

  const footerComponents = useMemo(
    () => importComponents(layouts?.default?.bottomModules ?? [], modulesConfigs),
    [layouts, modulesConfigs]
  );

  return (
    <div className="change-approval-layout" dir={direction || 'rtl'}>
      <header className="cal-header">
        <a href="/" className="cal-logo">
          <img src={getImagePathFromName(siteConfig?.logo)} alt="logo" />
        </a>
        {renderSiteLang({ withoutBorders: true })}
      </header>

      <main className="cal-content">{children}</main>

      <Suspense fallback={<Spin className="center" size="large" />}>
        {footerComponents.map(({ Component, ComponentName }, i) => (
          <div className={ComponentName} key={`${ComponentName}-${i}`}>
            {modulesConfigs[ComponentName]?.theme &&
              generateCssVariables(
                `.${ComponentName}`,
                modulesConfigs[ComponentName].theme as any
              )}
            <Component
              config={modulesConfigs[ComponentName]}
              componentStyle={classPriority(theme, modulesConfigs[ComponentName])}
            />
          </div>
        ))}
      </Suspense>
    </div>
  );
};

export default ChangeApprovalLayout;
